import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormRoot,
  FormTextarea,
  Label,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { CalendarClockIcon } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { toDateTimeLocalValue } from "@/shared/lib/datetime-local-form";
import { FormDateQuarterTimeField } from "@/shared/ui/FormDateQuarterTimeField";
import type { EventDTO } from "../../domain/contracts";
import { shiftEndDateByEventDelta } from "../../domain/services/awaiting-convert";
import { usePostponeEvent } from "../api/queries";

const PostponeFormSchema = z
  .object({
    indefinite: z.boolean(),
    newEventDate: z.string().optional(),
    newEndDate: z.string().optional(),
    reason: z.string().trim().min(1, "ຕ້ອງປ້ອນເຫດຜົນ").max(500),
  })
  .superRefine((value, ctx) => {
    if (!value.indefinite && !value.newEventDate?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["newEventDate"],
        message: "ຕ້ອງເລືອກວັນທີໃໝ່ ຫຼື ເລືອກເລື່ອນບໍ່ມີກຳນົດ",
      });
    }
  });

type PostponeFormValues = z.infer<typeof PostponeFormSchema>;

type EventPostponeDialogProps = {
  event: EventDTO;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function EventPostponeDialog({
  event,
  open,
  onOpenChange,
}: EventPostponeDialogProps) {
  const postpone = usePostponeEvent(event.id);
  const methods = RHF.useForm<PostponeFormValues>({
    resolver: zodResolver(PostponeFormSchema),
    defaultValues: {
      indefinite: false,
      newEventDate: toDateTimeLocalValue(event.eventDate),
      newEndDate: toDateTimeLocalValue(event.endDate) || "",
      reason: "",
    },
  });

  const indefinite = methods.watch("indefinite");

  useEffect(() => {
    if (!open) return;
    methods.reset({
      indefinite: Boolean(event.dateTbd),
      newEventDate: toDateTimeLocalValue(event.eventDate),
      newEndDate: toDateTimeLocalValue(event.endDate) || "",
      reason: "",
    });
  }, [open, event.id, event.dateTbd, event.eventDate, event.endDate, methods]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClockIcon className="size-5" />
            ເລື່ອນງານ
          </DialogTitle>
        </DialogHeader>

        <FormRoot
          methods={methods}
          onSubmit={async (values) => {
            try {
              if (values.indefinite) {
                await postpone.mutateAsync({
                  indefinite: true,
                  reason: values.reason,
                });
              } else {
                const newEventDate = new Date(values.newEventDate!);
                const newEndDate = values.newEndDate?.trim()
                  ? new Date(values.newEndDate).toISOString()
                  : shiftEndDateByEventDelta(
                      new Date(event.eventDate),
                      event.endDate ? new Date(event.endDate) : null,
                      newEventDate,
                    )?.toISOString() ?? null;

                await postpone.mutateAsync({
                  indefinite: false,
                  newEventDate: newEventDate.toISOString(),
                  newEndDate,
                  reason: values.reason,
                });
              }
              onOpenChange(false);
            } catch {
              // toast handled by mutation
            }
          }}
          className="space-y-4"
        >
          <p className="text-muted-foreground text-sm">
            ວັນປັດຈຸບັນ:{" "}
            <span className="font-medium text-foreground">
              {event.dateTbd
                ? "ຍັງບໍ່ກຳນົດ"
                : toDateTimeLocalValue(event.eventDate).replace("T", " ")}
            </span>
          </p>

          <div className="flex items-start gap-3 rounded-lg border px-3 py-2">
            <Checkbox
              id="postpone-indefinite"
              checked={indefinite}
              onCheckedChange={(checked) =>
                methods.setValue("indefinite", checked === true, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
            <div className="space-y-0.5">
              <Label htmlFor="postpone-indefinite" className="cursor-pointer">
                ເລື່ອນບໍ່ມີກຳນົດ
              </Label>
              <p className="text-muted-foreground text-xs">
                ຍັງບໍ່ມີວັນໃໝ່ — ຈະສະແດງສະຖານະ 「ຍັງບໍ່ກຳນົດ」 ໃນລາຍການ
              </p>
            </div>
          </div>

          {!indefinite ? (
            <>
              <FormDateQuarterTimeField
                name="newEventDate"
                label="ວັນເລີ່ມໃໝ່"
                required
              />
              <FormDateQuarterTimeField
                name="newEndDate"
                label="ວັນຈົບໃໝ່ (ຖ້າຫວ່າງ = ເລື່ອນຕາມ delta)"
                allowEmpty
              />
            </>
          ) : null}

          <FormTextarea
            name="reason"
            label="ເຫດຜົນ"
            rows={3}
            placeholder="ຕົວຢ່າງ: ລູກຄ້າຂໍເລື່ອນເນື່ອງຈາກສະຖານທີ່ຍັງບໍ່ພ້ອມ"
            required
          />

          <div className="rounded-lg border bg-muted/30 px-3 py-2 text-muted-foreground text-xs">
            <p className="mb-1 font-medium text-foreground">ຫຼັງບັນທຶກ — ກວດສອບ:</p>
            <ol className="list-decimal space-y-0.5 pl-4">
              <li>ເລື່ອນວັນຊ້ອມ (ຖ້າມີ)</li>
              <li>ກວດຕາຕະລາງໂຊ (ເລື່ອນຕາມ delta ແລ້ວ — ກວດເວລາ/ລຳດັບ)</li>
              <li>ກວດເອກະສານ / ກຳນົດຊຳລະ 7–14 ວັນ</li>
              <li>ແຈ້ງທີມນັກສະແດງ / ກວດໃບເບີກຈ່າຍ</li>
            </ol>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={postpone.isPending}>
              {postpone.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກການເລື່ອນ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
